/**
 * Step 09 — where the speaker's name card sits, and what face it is set in
 * (spec §2.2, `speaker`).
 *
 * The corner picker is a picture of the frame, never a stored name: the two
 * percentages are recomputed from the card's width every time
 * (`cornerToCardPct`), so a wider card still lands flush in its corner and the
 * layout stays the pair of numbers the render already understands.
 *
 * The face for the NAME is asked here rather than on the font step, because
 * here is where it is visible — the phone scrubs to 1.4 s so the card is on
 * screen while the row is being read (§10.3). "Same as captions" is the first
 * option and a different ANSWER, not a different value: it turns the mirror in
 * `state.ts` on, and picking a face by hand turns it off.
 *
 * Nothing is dispatched on mount.
 */
import { useEffect, useMemo, useState, type Dispatch } from "react";
import type { FontFamilyEntry } from "../../../api";
import {
  DEFAULT_CARD_WIDTH_PCT,
  cardPctToCorner,
  cornerToCardPct,
  type Corner,
} from "../../../lib/brandSetup/geometry";
import { LAYOUT_DEFAULTS, buildBaseLayout } from "../../../lib/brandSetup/payload";
import {
  FOUNDING_FONT_NAMES,
  defaultFontsForLanguage,
  effectiveDesign,
  effectiveFontNames,
  type BrandSetupAction,
  type BrandSetupState,
} from "../../../lib/brandSetup/state";
import { CornerPicker } from "../../../components/focus/essentials/CornerPicker";
import { Select } from "../../../components/ui/Select";
import { MONO_LABEL, ReelTag } from "../ui";

/** Anything the study read about the card — placement, size, timing. */
const CARD_FIELD_PREFIX = "layout.speakerCard.";
/** The "same as captions" option's value. Not a family name — no font is
 *  called this, so it can never collide with a real face. */
const SAME = "__same_as_captions__";

/**
 * How wide the card on the phone ACTUALLY is, as a % of the frame — measured,
 * not assumed.
 *
 * `cornerToCardPct` puts a right-hand card's LEFT edge at
 * `100 − inset − width`, so the width it is given decides whether the card
 * lands flush or hangs off the edge. The assumed default (35 %) is the
 * PLACEMENT box of a founding baked PNG; the live card the wizard previews
 * draws its name inside a transparent 2160-wide still, so its visible ink is
 * whatever the name and the face make it — at "Alex Rivera" that measured
 * ~45 %, and the top-right corner clipped the last 12 px of her speaker's
 * name off the frame (QA 2026-09-16, M2).
 *
 * So the step reads the real thing: a DOM Range over the card seam returns
 * the union of its glyph rects, and the distance from the card's own left
 * edge to the right edge of that ink IS the width the corner maths needs. It
 * polls while the step is open because the card is only on screen from 0.5 s
 * and because the name's face can change under it; it falls back to the
 * assumed default whenever the card is not currently drawn.
 */
/** Polling interval for the live measurement — 4 Hz is plenty to catch the
 *  card's 0.5 s entrance and any face change under it, without doing a
 *  `querySelector` + `Range` + three `getBoundingClientRect()` reads on
 *  every animation frame while the step is open. */
const MEASURE_INTERVAL_MS = 250;

function useLiveCardWidthPct(): number {
  const [pct, setPct] = useState(DEFAULT_CARD_WIDTH_PCT);
  useEffect(() => {
    const tick = (): void => {
      const node = document.querySelector<HTMLElement>('[data-studio-el="speaker"]');
      const frame = node?.offsetParent as HTMLElement | null;
      if (node && frame) {
        const range = document.createRange();
        range.selectNodeContents(node);
        const ink = range.getBoundingClientRect();
        const box = node.getBoundingClientRect();
        const frameWidth = frame.getBoundingClientRect().width;
        if (ink.width > 0 && frameWidth > 0) {
          const next = ((ink.right - box.left) / frameWidth) * 100;
          if (next > 0 && next < 200) setPct((p) => (Math.abs(p - next) > 0.25 ? next : p));
        }
      }
    };
    tick();
    const id = window.setInterval(tick, MEASURE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);
  return pct;
}

export function SpeakerStep(props: {
  state: BrandSetupState;
  dispatch: Dispatch<BrandSetupAction>;
  families: FontFamilyEntry[];
}): React.JSX.Element {
  const { state, dispatch, families } = props;

  const base = useMemo(() => buildBaseLayout(state), [state]);
  const baseCard = base.speakerCard ?? {
    xPct: LAYOUT_DEFAULTS.speakerCardXPct,
    yPct: LAYOUT_DEFAULTS.speakerCardYPct,
  };
  const moved = effectiveDesign(state)?.layout?.speakerCard;
  const cardWidthPct = useLiveCardWidthPct();
  const corner = cardPctToCorner(
    {
      xPct: moved?.xPct ?? baseCard.xPct,
      yPct: moved?.yPct ?? baseCard.yPct,
    },
    cardWidthPct,
  );

  // One flat list, in this order: the face uploaded in THIS setup, then the
  // catalogue, with the founding display face and the current one pinned to
  // the front when missing. A family the catalogue marks `uploaded` lives on
  // another brand (this one has no channel yet) — it stays in the list, with
  // a hint saying where it comes from, so it can be picked without a detour.
  const rows = useMemo(() => {
    const seed = defaultFontsForLanguage(state.language);
    const out: { family: string; hint?: string }[] = [];
    const add = (family: string, hint?: string, front = false): void => {
      if (out.some((r) => r.family === family)) return;
      if (front) out.unshift({ family, hint });
      else out.push({ family, hint });
    };
    const uploaded = state.fonts.uploaded?.family;
    if (uploaded) add(uploaded);
    for (const f of families) add(f.family, f.kind === "uploaded" ? "from your other brands" : undefined);
    // RTL leaves the role blank on purpose (the founding trio) — the face it
    // really renders in still has to be nameable and pickable.
    if (!seed.display) add(FOUNDING_FONT_NAMES.display, undefined, true);
    add(effectiveFontNames(state).display, undefined, true);
    return out;
  }, [families, state]);

  const value = state.displaySame ? SAME : effectiveFontNames(state).display;

  const chips = state.chips.filter((c) => c.field.startsWith(CARD_FIELD_PREFIX));
  const fromReel = chips.filter((c) => state.accepted[c.field] !== false);

  return (
    <div style={{ display: "grid", gap: 20 }}>
      {fromReel.length > 0 && (
        <ReelTag
          label="From your reel"
          onReset={() => fromReel.forEach((c) => dispatch({ type: "resetChip", field: c.field }))}
        />
      )}

      <div style={{ display: "grid", gap: 10 }}>
        <span style={MONO_LABEL}>Corner</span>
        <CornerPicker
          value={corner}
          onPick={(c) =>
            dispatch({
              type: "layoutPatch",
              patch: { speakerCard: cornerToCardPct(c as Corner, cardWidthPct) },
              label: "Moved the speaker's name",
            })
          }
        />
      </div>

      <div style={{ display: "grid", gap: 10, maxWidth: 320 }}>
        <span style={MONO_LABEL}>Font for the speaker&rsquo;s name</span>
        <Select
          data-autofocus
          aria-label="Font for the speaker's name"
          value={value}
          onChange={(v) => {
            if (v === SAME) {
              dispatch({ type: "patch", patch: { displaySame: true } });
              return;
            }
            // Both, in this order: naming a face IS the answer to "same as
            // captions?", and the reducer's own `fonts` case says so too —
            // being explicit keeps the two halves of the answer together.
            dispatch({ type: "patch", patch: { displaySame: false } });
            dispatch({ type: "fonts", fonts: { display: v } });
          }}
          size="lg"
          enterSubmits
          options={[
            { value: SAME, label: "Same as captions" },
            // Each family in its own face — the list is a specimen sheet.
            ...rows.map((r) => ({
              value: r.family,
              label: r.family,
              hint: r.hint,
              style: { fontFamily: `"${r.family}", var(--ql-font-sans)` },
            })),
          ]}
        />
      </div>
    </div>
  );
}
